import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { MAX_ROUTINE_INSTRUCTIONS_CHARS, type RoutineReadScope } from "@roomate/core";
import { ApiError, useActors, useMe, useRoutine, useSaveRoutine } from "../api.ts";
import { Page, PageHeader } from "../calm.tsx";
import {
  agentsForRoutine,
  emptyRoutineForm,
  providerNotice,
  READ_SCOPE_OPTIONS,
  routineBody,
  validateRoutineForm,
  type RoutineFormErrors,
  type RoutineFormValues,
} from "../lib/routine-form.ts";
import { canManageRoutines } from "../lib/routines.ts";
import { useCurrentProject } from "../project.tsx";
import { cx, ErrorNote, inputCls } from "../ui.tsx";

/*
 * New and edit routine: one form (docs/specs/routines.md §3.3). Errors are inline and name the field.
 * Only agents whose allowed areas fit the chosen reads are offered (§5.1); at least one read is required (§5.3).
 */

const zoneNames = (): string[] => {
  try {
    return (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.("timeZone") ?? [];
  } catch {
    return [];
  }
};

function FieldError({ id, message }: { id: string; message: string | undefined }) {
  if (!message) return null;
  return (
    <p id={id} role="alert" className="mt-1.5 text-[12.5px] text-high" data-testid={`${id}`}>
      {message}
    </p>
  );
}

function Label({ htmlFor, children, hint }: { htmlFor: string; children: React.ReactNode; hint?: string }) {
  return (
    <label htmlFor={htmlFor} className="block text-[13px] font-medium text-ink-2">
      {children}
      {hint && <span className="ml-2 text-[12px] font-normal text-ink-3">{hint}</span>}
    </label>
  );
}

/** Keyed by routine id, so going from one routine's edit page to another's starts from that routine's values. */
export function RoutineEditRoute() {
  const { id } = useParams();
  return <RoutineFormPage key={id} />;
}

export function RoutineFormPage() {
  const { id } = useParams();
  const editing = !!id;
  const navigate = useNavigate();
  const me = useMe();
  const actors = useActors().data;
  const { projectId: currentProjectId, project, ready } = useCurrentProject();
  const existing = useRoutine(id ?? "", true);
  const save = useSaveRoutine(id);
  const [values, setValues] = useState<RoutineFormValues>(emptyRoutineForm);
  const [loaded, setLoaded] = useState(!editing);
  const [submitted, setSubmitted] = useState(false);
  const zones = useMemo(zoneNames, []);

  // Fill the form once from the saved routine; later refetches must not overwrite what is being typed.
  useEffect(() => {
    const r = existing.data;
    // Wait for the refetch on mount so a cached copy from the detail page cannot be edited stale.
    if (!editing || loaded || !r || existing.isError || !existing.isFetchedAfterMount) return;
    setValues({ name: r.name, agentId: r.agentId, instructions: r.instructions, reads: r.reads, timeOfDay: r.timeOfDay, timezone: r.timezone, maxBudgetUsd: String(r.maxBudgetUsd) });
    setLoaded(true);
  }, [editing, loaded, existing.data, existing.isError, existing.isFetchedAfterMount]);

  const projectId = editing ? (existing.data?.projectId ?? "") : currentProjectId;
  const fitting = useMemo(() => agentsForRoutine(actors, projectId, values.reads), [actors, projectId, values.reads]);
  // A saved agent that no longer fits (or was deactivated) is shown as unchosen, so what the select shows is what is validated and sent.
  const agentId = fitting.some((a) => a.id === values.agentId) ? values.agentId : "";
  const errors: RoutineFormErrors = validateRoutineForm({ ...values, agentId }, fitting);
  const shown: RoutineFormErrors = submitted ? errors : {};
  const set = <K extends keyof RoutineFormValues>(key: K, value: RoutineFormValues[K]) => setValues((v) => ({ ...v, [key]: value }));
  const toggleRead = (scope: RoutineReadScope) => set("reads", values.reads.includes(scope) ? values.reads.filter((s) => s !== scope) : [...values.reads, scope]);

  const back = editing ? { label: "Routine", to: `/routines/${id}` } : { label: "Routines", to: "/routines" };
  if (me.data && !canManageRoutines(me.data))
    return (
      <Page>
        <PageHeader crumbs={[{ label: "Routines", to: "/routines" }]} title={editing ? "Edit routine" : "New routine"} />
        <p className="mt-6 text-[14px] text-ink-2" data-testid="routine-form-denied">
          Only admins can create or edit a routine.
        </p>
      </Page>
    );
  if (editing && existing.error instanceof ApiError && existing.error.status === 404)
    return (
      <Page>
        <p className="text-ink-3">Routine not found.</p>
      </Page>
    );
  // A failed background refetch must not replace a form the user is already filling in.
  if (editing && existing.error && (!existing.data || !loaded))
    return (
      <Page>
        <ErrorNote error={existing.error} />
      </Page>
    );
  if (!loaded || !ready || !actors || !me.data)
    return (
      <Page>
        <p className="text-ink-3">Loading…</p>
      </Page>
    );

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    if (Object.keys(errors).length > 0 || !projectId) return;
    const body = routineBody({ ...values, agentId });
    save.mutate(editing ? body : { ...body, projectId }, { onSuccess: (r) => navigate(`/routines/${r.id}`) });
  };

  const unfitNote = values.reads.length > 0 && fitting.length === 0;

  return (
    <Page>
      <PageHeader crumbs={[{ label: "Routines", to: "/routines" }, ...(editing ? [back] : [])]} title={editing ? "Edit routine" : "New routine"} />
      <form onSubmit={submit} noValidate className="mt-6 max-w-2xl space-y-6" data-testid="routine-form">
        {!editing && (
          <p className="text-[13px] text-ink-2" data-testid="routine-project">
            {projectId ? <>In project <span className="font-medium">{project?.name ?? projectId}</span></> : "Create a project before adding a routine."}
          </p>
        )}
        <div>
          <Label htmlFor="routine-name">Name</Label>
          <input id="routine-name" className={cx(inputCls, "mt-1.5")} value={values.name} onChange={(e) => set("name", e.target.value)} aria-invalid={!!shown.name} aria-describedby={shown.name ? "routine-name-error" : undefined} data-testid="routine-name" autoFocus />
          <FieldError id="routine-name-error" message={shown.name} />
        </div>

        <fieldset aria-describedby={shown.reads ? "routine-reads-error" : undefined}>
          <legend className="text-[13px] font-medium text-ink-2">Reads</legend>
          <p className="mt-0.5 text-[12.5px] text-ink-3">What each run may read from this project. A routine needs at least one.</p>
          <div className="mt-2 space-y-2" data-testid="routine-reads">
            {READ_SCOPE_OPTIONS.map((o) => (
              <label key={o.scope} className="flex items-start gap-2 text-[13.5px]">
                <input type="checkbox" className="mt-1" checked={values.reads.includes(o.scope)} onChange={() => toggleRead(o.scope)} data-testid={`routine-read-${o.scope.slice("roomate:".length)}`} />
                <span>
                  {o.label}
                  <span className="block text-[12.5px] text-ink-3">{o.hint}</span>
                </span>
              </label>
            ))}
          </div>
          <FieldError id="routine-reads-error" message={shown.reads} />
          <div role="status">
            {values.reads.length > 0 && (
              <p className="mt-3 rounded-md bg-ink/[0.045] px-3 py-2 text-[13px] text-ink-2" data-testid="routine-provider-notice">
                {providerNotice(values.reads)}
              </p>
            )}
          </div>
        </fieldset>

        <div>
          <Label htmlFor="routine-agent" hint={values.reads.length === 0 ? "choose reads first to see which agents fit" : undefined}>
            Agent
          </Label>
          <select id="routine-agent" className={cx(inputCls, "mt-1.5")} value={agentId} onChange={(e) => set("agentId", e.target.value)} aria-invalid={!!shown.agentId} aria-describedby={shown.agentId ? "routine-agent-error" : undefined} data-testid="routine-agent">
            <option value="">{unfitNote ? "No agent fits" : "Choose an agent"}</option>
            {fitting.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
          <FieldError id="routine-agent-error" message={shown.agentId} />
        </div>

        <div>
          <Label htmlFor="routine-instructions" hint={`${values.instructions.length.toLocaleString("en")} / ${MAX_ROUTINE_INSTRUCTIONS_CHARS.toLocaleString("en")}`}>
            Instructions
          </Label>
          <textarea id="routine-instructions" className={cx(inputCls, "mt-1.5")} rows={8} value={values.instructions} onChange={(e) => set("instructions", e.target.value)} aria-invalid={!!shown.instructions} aria-describedby={shown.instructions ? "routine-instructions-error" : undefined} data-testid="routine-instructions" />
          <FieldError id="routine-instructions-error" message={shown.instructions} />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="routine-time">Time of day</Label>
            <input id="routine-time" type="time" className={cx(inputCls, "mt-1.5")} value={values.timeOfDay} onChange={(e) => set("timeOfDay", e.target.value)} aria-invalid={!!shown.timeOfDay} aria-describedby={shown.timeOfDay ? "routine-time-error" : undefined} data-testid="routine-time" />
            <FieldError id="routine-time-error" message={shown.timeOfDay} />
          </div>
          <div>
            <Label htmlFor="routine-timezone">Time zone</Label>
            <input id="routine-timezone" list="routine-zones" className={cx(inputCls, "mt-1.5")} value={values.timezone} onChange={(e) => set("timezone", e.target.value)} aria-invalid={!!shown.timezone} aria-describedby={shown.timezone ? "routine-timezone-error" : undefined} data-testid="routine-timezone" />
            <datalist id="routine-zones">
              {zones.map((z) => (
                <option key={z} value={z} />
              ))}
            </datalist>
            <FieldError id="routine-timezone-error" message={shown.timezone} />
          </div>
        </div>

        <div className="max-w-[12rem]">
          <Label htmlFor="routine-budget" hint="per run, USD">
            Budget cap
          </Label>
          <input id="routine-budget" inputMode="decimal" className={cx(inputCls, "mt-1.5 tabular-nums")} value={values.maxBudgetUsd} onChange={(e) => set("maxBudgetUsd", e.target.value)} aria-invalid={!!shown.maxBudgetUsd} aria-describedby={shown.maxBudgetUsd ? "routine-budget-error" : undefined} data-testid="routine-budget" />
          <FieldError id="routine-budget-error" message={shown.maxBudgetUsd} />
        </div>

        {save.error && <ErrorNote error={save.error} />}
        <div className="flex items-center gap-3">
          <button type="submit" disabled={save.isPending} className="rounded-full bg-ink px-5 py-2 text-[13.5px] text-paper hover:opacity-90 disabled:opacity-50" data-testid="routine-save">
            {save.isPending ? "Saving…" : editing ? "Save changes" : "Create routine"}
          </button>
          <Link to={editing ? `/routines/${id}` : "/routines"} className="text-[13.5px] text-ink-2 underline decoration-rule hover:text-ink" data-testid="routine-cancel">
            Cancel
          </Link>
        </div>
      </form>
    </Page>
  );
}
